import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Traffic as TrafficIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Route as RouteIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  DirectionsWalk as WalkIcon,
  DirectionsCar as CarIcon,
  Thermostat as TemperatureIcon,
  VerifiedUser as VerifiedUserIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';

interface TransporterDocument {
  id: string;
  name: string;
  type: 'identity' | 'license' | 'insurance' | 'vehicle' | 'other';
  status: 'pending' | 'verified' | 'rejected';
  uploadDate: string;
  expiryDate?: string;
  verificationDate?: string;
  file: File | null;
  required: boolean;
  description: string;
}

interface TransporterVerificationProps {
  transporterId?: string;
  onVerificationComplete?: (verified: boolean) => void;
  isAdmin?: boolean;
}

/**
 * TransporterVerification component for verifying transporters
 * Handles document uploads, verification checks, and compliance with regulations
 * @param transporterId - Optional ID of the transporter to verify
 * @param onVerificationComplete - Callback function when verification is complete
 * @param isAdmin - Boolean indicating if the current user is an admin
 * @returns React component for transporter verification
 */
const TransporterVerification: React.FC<TransporterVerificationProps> = ({
  transporterId,
  onVerificationComplete,
  isAdmin = false,
}) => {
  // State for tracking the current step in the verification process
  const [activeStep, setActiveStep] = useState(0);
  
  // State for loading status
  const [loading, setLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for document preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<TransporterDocument | null>(null);
  
  // State for required documents
  const [documents, setDocuments] = useState<TransporterDocument[]>([
    {
      id: '1',
      name: 'National ID / Passport',
      type: 'identity',
      status: 'pending',
      uploadDate: '',
      file: null,
      required: true,
      description: 'Valid government-issued identification document',
    },
    {
      id: '2',
      name: 'Driver\'s License',
      type: 'license',
      status: 'pending',
      uploadDate: '',
      expiryDate: '',
      file: null,
      required: true,
      description: 'Valid driver\'s license with appropriate class for vehicle type',
    },
    {
      id: '3',
      name: 'Vehicle Registration',
      type: 'vehicle',
      status: 'pending',
      uploadDate: '',
      expiryDate: '',
      file: null,
      required: true,
      description: 'Vehicle registration certificate issued by NTSA',
    },
    {
      id: '4',
      name: 'Vehicle Insurance',
      type: 'insurance',
      status: 'pending',
      uploadDate: '',
      expiryDate: '',
      file: null,
      required: true,
      description: 'Comprehensive vehicle insurance policy',
    },
    {
      id: '5',
      name: 'Goods in Transit Insurance',
      type: 'insurance',
      status: 'pending',
      uploadDate: '',
      expiryDate: '',
      file: null,
      required: true,
      description: 'Insurance covering goods during transportation',
    },
    {
      id: '6',
      name: 'PSV License (if applicable)',
      type: 'license',
      status: 'pending',
      uploadDate: '',
      expiryDate: '',
      file: null,
      required: false,
      description: 'Public Service Vehicle license for passenger transport',
    },
    {
      id: '7',
      name: 'Good Conduct Certificate',
      type: 'other',
      status: 'pending',
      uploadDate: '',
      file: null,
      required: true,
      description: 'Certificate of good conduct from the police',
    },
  ]);
  
  // State for transporter details
  const [transporterDetails, setTransporterDetails] = useState({
    name: '',
    phone: '',
    email: '',
    idNumber: '',
    licenseNumber: '',
    licenseClass: '',
    vehicleType: '',
    vehicleRegistration: '',
    vehicleCapacity: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: '',
    goodsInsuranceProvider: '',
    goodsInsurancePolicyNumber: '',
    goodsInsuranceExpiryDate: '',
    hasAccident: false,
    accidentDetails: '',
    criminalRecord: false,
    criminalRecordDetails: '',
    agreeToTerms: false,
    agreeToBackgroundCheck: false,
  });
  
  // Steps in the verification process
  const steps = [
    'Personal Information',
    'Vehicle Details',
    'Insurance Information',
    'Document Upload',
    'Background Check',
    'Review & Submit'
  ];
  
  // Vehicle types for dropdown
  const vehicleTypes = [
    'Motorcycle',
    'Car',
    'Van',
    'Pickup Truck',
    'Truck (1-3 ton)',
    'Truck (3-7 ton)',
    'Truck (7+ ton)',
    'Refrigerated Truck',
    'Tanker'
  ];
  
  // License classes for dropdown
  const licenseClasses = [
    'A - Motorcycles',
    'B - Cars and light vans',
    'C - Commercial vehicles',
    'D - Buses',
    'E - Heavy goods vehicles',
    'F - Tractors and special vehicles',
  ];
  
  /**
   * Handles changes to form fields
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleChange = (field: string, value: any) => {
    setTransporterDetails({
      ...transporterDetails,
      [field]: value
    });
  };
  
  /**
   * Handles file uploads for document verification
   * @param documentId - The ID of the document being uploaded
   * @param file - The file object from the input
   */
  const handleFileUpload = (documentId: string, file: File | null) => {
    setDocuments(documents.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          file,
          uploadDate: file ? new Date().toISOString() : '',
          status: file ? 'pending' : doc.status
        };
      }
      return doc;
    }));
  };
  
  /**
   * Handles document expiry date changes
   * @param documentId - The ID of the document
   * @param date - The expiry date string
   */
  const handleExpiryDateChange = (documentId: string, date: string) => {
    setDocuments(documents.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          expiryDate: date
        };
      }
      return doc;
    }));
  };
  
  /**
   * Opens the document preview dialog
   * @param document - The document to preview
   */
  const handlePreviewDocument = (document: TransporterDocument) => {
    setPreviewDocument(document);
    setPreviewOpen(true);
  };
  
  /**
   * Closes the document preview dialog
   */
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
  };
  
  /**
   * Moves to the next step in the verification process
   */
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  /**
   * Moves to the previous step in the verification process
   */
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  /**
   * Verifies a document (admin only)
   * @param documentId - The ID of the document to verify
   * @param status - The verification status to set
   */
  const handleVerifyDocument = (documentId: string, status: 'verified' | 'rejected') => {
    if (!isAdmin) return;
    
    setDocuments(documents.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          status,
          verificationDate: new Date().toISOString()
        };
      }
      return doc;
    }));
  };
  
  /**
   * Submits the verification form
   * In a real application, this would send the data to a server
   */
  const handleSubmit = () => {
    setLoading(true);
    setError(null);
    
    // Simulate API call with setTimeout
    setTimeout(() => {
      // Check if all required documents are uploaded
      const missingDocuments = documents
        .filter(doc => doc.required && !doc.file)
        .map(doc => doc.name);
      
      if (missingDocuments.length > 0) {
        setError(`Missing required documents: ${missingDocuments.join(', ')}`);
        setLoading(false);
        return;
      }
      
      // In a real app, this would send the data to the server
      console.log('Submitting transporter verification:', transporterDetails);
      console.log('Documents:', documents);
      
      setSuccess('Verification submitted successfully! Your documents will be reviewed within 24-48 hours.');
      setLoading(false);
      
      // Call the callback if provided
      if (onVerificationComplete) {
        onVerificationComplete(true);
      }
      
      // Move to a success step
      setActiveStep(steps.length);
    }, 1500);
  };
  
  /**
   * Checks if the current step is complete and valid
   * @returns Boolean indicating if the user can proceed to the next step
   */
  const isStepValid = () => {
    switch (activeStep) {
      case 0: // Personal Information
        return !!transporterDetails.name && !!transporterDetails.phone && 
               !!transporterDetails.email && !!transporterDetails.idNumber;
      case 1: // Vehicle Details
        return !!transporterDetails.vehicleType && !!transporterDetails.vehicleRegistration && 
               !!transporterDetails.licenseNumber && !!transporterDetails.licenseClass;
      case 2: // Insurance Information
        return !!transporterDetails.insuranceProvider && !!transporterDetails.insurancePolicyNumber && 
               !!transporterDetails.insuranceExpiryDate && !!transporterDetails.goodsInsuranceProvider;
      case 3: // Document Upload
        return documents.filter(doc => doc.required).every(doc => !!doc.file);
      case 4: // Background Check
        return transporterDetails.agreeToBackgroundCheck;
      case 5: // Review & Submit
        return transporterDetails.agreeToTerms;
      default:
        return true;
    }
  };
  
  /**
   * Renders the content for the current step
   * @returns JSX for the current step's form fields
   */
  const getStepContent = () => {
    switch (activeStep) {
      case 0: // Personal Information
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={transporterDetails.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID Number / Passport Number"
                value={transporterDetails.idNumber}
                onChange={(e) => handleChange('idNumber', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={transporterDetails.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={transporterDetails.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Driver's License Number"
                value={transporterDetails.licenseNumber}
                onChange={(e) => handleChange('licenseNumber', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>License Class</InputLabel>
                <Select
                  value={transporterDetails.licenseClass}
                  label="License Class"
                  onChange={(e) => handleChange('licenseClass', e.target.value)}
                >
                  {licenseClasses.map((licenseClass) => (
                    <MenuItem key={licenseClass} value={licenseClass}>
                      {licenseClass}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      
      case 1: // Vehicle Details
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Vehicle Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={transporterDetails.vehicleType}
                  label="Vehicle Type"
                  onChange={(e) => handleChange('vehicleType', e.target.value)}
                >
                  {vehicleTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle Registration Number"
                value={transporterDetails.vehicleRegistration}
                onChange={(e) => handleChange('vehicleRegistration', e.target.value)}
                required
                helperText="As shown on the vehicle registration certificate"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle Capacity"
                value={transporterDetails.vehicleCapacity}
                onChange={(e) => handleChange('vehicleCapacity', e.target.value)}
                helperText="e.g., '2 tons', '5 passengers', etc."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Your vehicle must meet all NTSA requirements for commercial transportation. 
                  Make sure your vehicle registration is current and the vehicle is in good condition.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      
      case 2: // Insurance Information
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Insurance Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle Insurance Provider"
                value={transporterDetails.insuranceProvider}
                onChange={(e) => handleChange('insuranceProvider', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Insurance Policy Number"
                value={transporterDetails.insurancePolicyNumber}
                onChange={(e) => handleChange('insurancePolicyNumber', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Insurance Expiry Date"
                type="date"
                value={transporterDetails.insuranceExpiryDate}
                onChange={(e) => handleChange('insuranceExpiryDate', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Goods in Transit Insurance
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Goods Insurance Provider"
                value={transporterDetails.goodsInsuranceProvider}
                onChange={(e) => handleChange('goodsInsuranceProvider', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Goods Insurance Policy Number"
                value={transporterDetails.goodsInsurancePolicyNumber}
                onChange={(e) => handleChange('goodsInsurancePolicyNumber', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Goods Insurance Expiry Date"
                type="date"
                value={transporterDetails.goodsInsuranceExpiryDate}
                onChange={(e) => handleChange('goodsInsuranceExpiryDate', e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="body2">
                  Both vehicle insurance and goods in transit insurance are mandatory for all transporters. 
                  Your insurance must be valid throughout your service period with our platform.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      
      case 3: // Document Upload
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Document Upload
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please upload clear, legible scans or photos of the following documents. 
                All documents must be valid and not expired.
              </Typography>
            </Grid>
            
            {documents.map((document) => (
              <Grid item xs={12} key={document.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {document.name}
                          {document.required && (
                            <Typography component="span" color="error" sx={{ ml: 1 }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {document.description}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                          {document.status === 'verified' ? (
                            <Chip 
                              icon={<CheckIcon />} 
                              label="Verified" 
                              color="success" 
                              size="small" 
                            />
                          ) : document.status === 'rejected' ? (
                            <Chip 
                              icon={<WarningIcon />} 
                              label="Rejected" 
                              color="error" 
                              size="small" 
                            />
                          ) : document.file ? (
                            <Chip 
                              label="Pending Verification" 
                              color="warning" 
                              size="small" 
                            />
                          ) : null}
                          
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadIcon />}
                            size="small"
                          >
                            Upload
                            <input
                              type="file"
                              hidden
                              accept="image/*, application/pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                handleFileUpload(document.id, file);
                              }}
                            />
                          </Button>
                          
                          {document.file && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePreviewDocument(document)}
                            >
                              Preview
                            </Button>
                          )}
                        </Box>
                        
                        {document.file && (
                          <Typography variant="caption" display="block" sx={{ textAlign: 'right', mt: 1 }}>
                            Uploaded: {new Date(document.uploadDate).toLocaleDateString()}
                          </Typography>
                        )}
                        
                        {document.expiryDate && (
                          <TextField
                            label="Expiry Date"
                            type="date"
                            size="small"
                            value={document.expiryDate}
                            onChange={(e) => handleExpiryDateChange(document.id, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mt: 1, width: '100%' }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  All documents will be verified by our team. The verification process typically takes 24-48 hours.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      
      case 4: // Background Check
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Background Check
              </Typography>
              <Typography variant="body2" paragraph>
                We conduct background checks on all transporters to ensure the safety and security of our platform.
                Please answer the following questions truthfully.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={transporterDetails.hasAccident}
                    onChange={(e) => handleChange('hasAccident', e.target.checked)}
                  />
                }
                label="Have you been involved in any traffic accidents in the past 3 years?"
              />
            </Grid>
            
            {transporterDetails.hasAccident && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Please provide details about the accident(s)"
                  multiline
                  rows={3}
                  value={transporterDetails.accidentDetails}
                  onChange={(e) => handleChange('accidentDetails', e.target.value)}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={transporterDetails.criminalRecord}
                    onChange={(e) => handleChange('criminalRecord', e.target.checked)}
                  />
                }
                label="Do you have any criminal record?"
              />
            </Grid>
            
            {transporterDetails.criminalRecord && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Please provide details about your criminal record"
                  multiline
                  rows={3}
                  value={transporterDetails.criminalRecordDetails}
                  onChange={(e) => handleChange('criminalRecordDetails', e.target.value)}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={transporterDetails.agreeToBackgroundCheck}
                    onChange={(e) => handleChange('agreeToBackgroundCheck', e.target.checked)}
                    required
                  />
                }
                label="I consent to a background check, including driving record and criminal history verification."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Background checks are conducted in compliance with all applicable laws and regulations.
                  Your information will be handled confidentially and securely.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      
      case 5: // Review & Submit
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review & Submit
              </Typography>
              <Typography variant="body2" paragraph>
                Please review all the information you've provided before submitting your verification request.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1">{transporterDetails.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">ID Number</Typography>
                      <Typography variant="body1">{transporterDetails.idNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{transporterDetails.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{transporterDetails.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">License Number</Typography>
                      <Typography variant="body1">{transporterDetails.licenseNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">License Class</Typography>
                      <Typography variant="body1">{transporterDetails.licenseClass}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Vehicle Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Vehicle Type</Typography>
                      <Typography variant="body1">{transporterDetails.vehicleType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Registration Number</Typography>
                      <Typography variant="body1">{transporterDetails.vehicleRegistration}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Insurance Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Vehicle Insurance Provider</Typography>
                      <Typography variant="body1">{transporterDetails.insuranceProvider}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Insurance Policy Number</Typography>
                      <Typography variant="body1">{transporterDetails.insurancePolicyNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Insurance Expiry Date</Typography>
                      <Typography variant="body1">{transporterDetails.insuranceExpiryDate}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Goods in Transit Insurance</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Goods Insurance Provider</Typography>
                      <Typography variant="body1">{transporterDetails.goodsInsuranceProvider}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Goods Insurance Policy Number</Typography>
                      <Typography variant="body1">{transporterDetails.goodsInsurancePolicyNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Goods Insurance Expiry Date</Typography>
                      <Typography variant="body1">{transporterDetails.goodsInsuranceExpiryDate}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={transporterDetails.agreeToTerms}
                    onChange={(e) => handleChange('agreeToTerms', e.target.checked)}
                    required
                  />
                }
                label="I consent to the terms and conditions of the verification process."
              />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Transporter Verification
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Verify transporter details and documentation. All transporters must have valid licenses, insurance, and vehicle documentation.
        </Alert>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid item xs={12}>
            <div>
              {isStepValid() ? getStepContent() : 'Please fill in all required fields'}
            </div>
          </Grid>
          <Grid item xs={12}>
            <div>
              {activeStep === steps.length ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Next'}
                </Button>
              )}
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TransporterVerification;